import { ArrowRight, ArrowUpRight, MousePointerClick, Repeat, ShieldCheck, Target, TrendingUp } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { T, FONT, mix } from '../theme';
import { Eyebrow, H1, Stage, Sub } from '../primitives';
import { OptimizationDeepDive } from '../plan/OptimizationDeepDive';
import type { OptimizationVM, OptIcon } from '../../formatters/deckContent';
import type { ReportEnrichment, WebReportPayload } from '../../types/webReport';

const ICONS: Record<OptIcon, LucideIcon> = {
  shield: ShieldCheck,
  trend: TrendingUp,
  repeat: Repeat,
  target: Target,
};

const COUNT_WORDS = ['One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight'];

interface Props {
  optimizations: OptimizationVM[];
  opt: number | null;
  openOptimization: (i: number) => void;
  setOpt: (i: number | null) => void;
  payload: WebReportPayload;
  enrichment: ReportEnrichment | null;
  interactive: boolean;
}

/** Act 4 — Plan: the optimization card grid, or the active deep dive. */
export function ActPlan({ optimizations, opt, openOptimization, setOpt, payload, enrichment, interactive }: Props) {
  if (opt !== null && optimizations[opt]) {
    return (
      <OptimizationDeepDive
        vm={optimizations[opt]}
        index={opt}
        count={optimizations.length}
        onBack={() => setOpt(null)}
        onSwitch={openOptimization}
        payload={payload}
        enrichment={enrichment}
        interactive={interactive}
      />
    );
  }

  const count = optimizations.length;
  const countWord = COUNT_WORDS[count - 1] || String(count);
  const title =
    count === 1 ? 'One optimization that transforms your plan' : `${countWord} optimizations that transform your plan`;

  return (
    <Stage>
      <Eyebrow>What we recommend</Eyebrow>
      <H1>{title}</H1>
      <Sub>
        {interactive && (
          <>
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                background: T.tealSoft,
                color: T.tealDeep,
                padding: '4px 11px',
                borderRadius: 20,
                fontSize: 13,
                fontWeight: 600,
              }}
            >
              <MousePointerClick size={15} /> Tap any card to explore it
            </span>
            {'  '}
          </>
        )}
        Each is shown at the scale where its real effect is clearest.
      </Sub>
      <div style={{ display: 'grid', gridTemplateColumns: `repeat(${Math.min(3, Math.max(1, count))},1fr)`, gap: 18 }}>
        {optimizations.map((o, i) => {
          const Icon = ICONS[o.icon];
          return (
            <button
              key={o.key}
              onClick={() => openOptimization(i)}
              style={{
                position: 'relative',
                textAlign: 'left',
                cursor: 'pointer',
                fontFamily: FONT,
                background: T.card,
                borderRadius: 12,
                boxShadow: '0 2px 10px rgba(27,42,74,.07)',
                border: `1.5px solid ${T.grayLine}`,
                padding: 22,
                display: 'flex',
                flexDirection: 'column',
                transition: 'transform .15s, box-shadow .15s, border-color .15s',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-3px)';
                e.currentTarget.style.boxShadow = `0 10px 24px ${mix(T.teal, 18)}`;
                e.currentTarget.style.borderColor = 'var(--color-accent)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'none';
                e.currentTarget.style.boxShadow = '0 2px 10px rgba(27,42,74,.07)';
                e.currentTarget.style.borderColor = 'var(--color-cream-soft)';
              }}
            >
              {/* corner click glyph — gently pulses to signal it's interactive */}
              {interactive && (
                <div
                  style={{
                    position: 'absolute',
                    top: 16,
                    right: 16,
                    width: 28,
                    height: 28,
                    borderRadius: 8,
                    background: T.teal,
                    color: '#fff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    animation: 'pulseGlow 2.4s ease-in-out infinite',
                    animationDelay: `${i * 0.4}s`,
                  }}
                >
                  <ArrowUpRight size={17} />
                </div>
              )}
              <div
                style={{
                  width: 42,
                  height: 42,
                  borderRadius: 10,
                  background: T.tealSoft,
                  color: T.teal,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginBottom: 14,
                }}
              >
                <Icon size={22} />
              </div>
              <div style={{ fontWeight: 700, color: T.navy, fontSize: 16, marginBottom: 6, lineHeight: 1.25, paddingRight: 30 }}>
                {o.title}
              </div>
              <div style={{ fontSize: 13, color: T.gray, lineHeight: 1.5, marginBottom: 16, flex: 1 }}>{o.blurb}</div>
              <div style={{ background: T.growthSoft, borderRadius: 9, padding: '12px 14px', textAlign: 'center' }}>
                <div style={{ fontSize: 24, fontWeight: 700, color: T.growth }}>{o.valueAddedLabel}</div>
                <div style={{ fontSize: 11.5, color: T.growth, fontWeight: 600 }}>total value added</div>
              </div>
              <div
                style={{
                  marginTop: 14,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 6,
                  background: T.navy,
                  color: '#fff',
                  borderRadius: 8,
                  padding: '10px 14px',
                  fontSize: 13,
                  fontWeight: 600,
                }}
              >
                See how it works <ArrowRight size={15} />
              </div>
            </button>
          );
        })}
      </div>
    </Stage>
  );
}
